
import { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface ClaimCompanyBoxProps {
  companyName: string;
  companyId?: string;
}

const ClaimCompanyBox = ({ companyName, companyId }: ClaimCompanyBoxProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: user?.email || '',
    jobTitle: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.email || !formData.jobTitle || !formData.message) {
      toast({
        title: "Missing information",
        description: "Please fill in all fields",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Using 'any' temporarily to bypass TypeScript errors
      const { error } = await supabase
        .from('company_claim_requests' as any)
        .insert({
          company_name: companyName,
          company_id: companyId,
          full_name: formData.fullName,
          email: formData.email,
          job_title: formData.jobTitle,
          message: formData.message,
          user_id: user?.id
        });
      
      if (error) throw error;
      
      toast({
        title: "Request submitted",
        description: "Your request to claim this company has been submitted. An administrator will review it shortly.",
      });
      
      setIsOpen(false);
      setFormData({
        fullName: '',
        email: user?.email || '',
        jobTitle: '',
        message: '',
      });
    } catch (error) {
      console.error("Error submitting claim request:", error);
      toast({
        title: "Request failed",
        description: "There was a problem submitting your request. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-redflags-light/20">
      <div className="flex flex-col space-y-3">
        <h3 className="text-lg font-medium text-redflags-dark">
          Claim This Company Profile
        </h3>
        
        <p className="text-sm text-redflags-gray">
          {isOpen 
            ? "Please provide your details below to request management access to this company profile."
            : "Are you an employee of this company? Request to manage this company profile by sending a message to our administrators."}
        </p>
        
        {!isOpen ? (
          <Button 
            onClick={() => setIsOpen(true)}
            className="bg-redflags-red hover:bg-redflags-red/90 text-white w-full md:w-auto"
          >
            <Send className="w-4 h-4 mr-2" /> 
            Request Access
          </Button>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="fullName" className="text-sm font-medium text-gray-700">Full Name</label>
                <Input
                  id="fullName"
                  name="fullName"
                  placeholder="Your full name"
                  value={formData.fullName}
                  onChange={handleChange}
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-gray-700">Email</label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Your work email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <label htmlFor="jobTitle" className="text-sm font-medium text-gray-700">Job Title</label>
                <Input
                  id="jobTitle"
                  name="jobTitle"
                  placeholder="Your position at the company"
                  value={formData.jobTitle}
                  onChange={handleChange}
                />
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <label htmlFor="message" className="text-sm font-medium text-gray-700">Message to Administrators</label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Please explain why you should be granted management access to this company profile..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-redflags-red hover:bg-redflags-red/90 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Send Request"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ClaimCompanyBox;
